import { Link, NavLink, Outlet } from 'react-router-dom'
import { StepUpReturnNotice } from '@/components/admin/step-up/StepUpReturnNotice'
import {
  LayoutDashboard,
  FileText,
  Database,
  Cpu,
  Wrench,
  DollarSign,
  Mail,
  Building2,
  Activity,
  Eye,
  Layers,
  Library,
  Inbox,
  ScrollText,
  ChevronLeft,
  Shield,
  BarChart2,
  Bug,
  BookOpen,
  ClipboardList,
  CalendarClock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Breadcrumbs } from '@/components/shared/Breadcrumbs'
import { isVulnsAdminEnabled } from '@/hooks/useAdminVulnerabilities'
import { useAdminBenchmarksPendingCount } from '@/hooks/useBenchmarkQueries'

type AdminNavItem = {
  to: string
  icon: typeof LayoutDashboard
  label: string
  end: boolean
  /** When set, renders the current count as a right-aligned pill badge. */
  badgeCount?: number
}

type AdminSection = {
  label: string
  items: AdminNavItem[]
}

function getAdminSections(pendingBenchmarkCount: number | undefined): AdminSection[] {
  const contentItems: AdminNavItem[] = [
    { to: '/admin/content', icon: FileText, label: 'Content', end: false },
    { to: '/admin/stories', icon: Layers, label: 'Stories', end: false },
    { to: '/admin/sources', icon: Database, label: 'Sources', end: false },
    { to: '/admin/vendors', icon: Building2, label: 'Vendors', end: false },
  ]
  // SP1 flag retired — benchmarks admin sidebar items are always visible to admins.
  contentItems.push({
    to: '/admin/benchmarks',
    icon: Library,
    label: 'Benchmarks',
    end: true,
  })
  // SP2 — pending review queue lives under the Benchmarks group. The badge
  // count comes from `useAdminBenchmarksPendingCount()` polled every 60s.
  contentItems.push({
    to: '/admin/benchmarks/pending',
    icon: Inbox,
    label: 'Pending Review',
    end: false,
    badgeCount: pendingBenchmarkCount,
  })
  if (isVulnsAdminEnabled()) {
    contentItems.push({
      to: '/admin/vulnerabilities',
      icon: Bug,
      label: 'Vulnerabilities',
      end: false,
    })
  }
  // Frameworks provenance — always visible; no feature flag needed
  contentItems.push({
    to: '/admin/frameworks',
    icon: BookOpen,
    label: 'Frameworks',
    end: false,
  })
  contentItems.push({
    to: '/admin/regulatory-briefs',
    icon: ClipboardList,
    label: 'Regulatory Briefs',
    end: false,
  })
  contentItems.push({
    to: '/admin/regulatory-deadlines',
    icon: CalendarClock,
    label: 'Deadlines',
    end: true,
  })
  contentItems.push({
    to: '/admin/regulatory-deadlines/new',
    icon: CalendarClock,
    label: 'New Deadline',
    end: false,
  })
  contentItems.push({
    to: '/admin/regulatory/digest-runs',
    icon: Mail,
    label: 'Digest Runs',
    end: false,
  })
  contentItems.push({
    to: '/admin/legal-content',
    icon: ScrollText,
    label: 'Legal Content',
    end: true,
  })
  return [
    {
      label: 'OVERVIEW',
      items: [
        { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
      ],
    },
    {
      label: 'CONTENT',
      items: contentItems,
    },
    {
      label: 'AI & Models',
      items: [
        { to: '/admin/ai/providers', icon: Cpu, label: 'Providers', end: false },
        { to: '/admin/ai/tasks', icon: Wrench, label: 'Tasks', end: false },
        { to: '/admin/ai/costs', icon: DollarSign, label: 'Cost & Usage', end: false },
      ],
    },
    {
      label: 'OPERATIONS',
      items: [
        { to: '/admin/email', icon: Mail, label: 'Email', end: false },
        { to: '/admin/system', icon: Activity, label: 'System', end: false },
        { to: '/admin/observability', icon: Eye, label: 'Observability', end: false },
        { to: '/admin/analytics', icon: BarChart2, label: 'Analytics', end: false },
        { to: '/admin/audit', icon: ScrollText, label: 'Audit log', end: false },
      ],
    },
  ]
}

export function AdminLayout() {
  // SP2 — pending count polled every 60s; badge renders when count > 0.
  const pendingCountQuery = useAdminBenchmarksPendingCount()
  const adminSections = getAdminSections(pendingCountQuery.data?.count)
  const renderNavItem = ({ to, icon: Icon, label, end, badgeCount }: AdminNavItem) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'border-destructive bg-sidebar-accent text-sidebar-accent-foreground'
            : 'border-transparent text-sidebar-foreground hover:bg-sidebar-accent/50',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1">{label}</span>
      {typeof badgeCount === 'number' && badgeCount > 0 ? (
        <span
          data-testid="sidebar-pending-badge"
          className="ml-auto rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive"
          aria-label={`${badgeCount} pending`}
        >
          {badgeCount}
        </span>
      ) : null}
    </NavLink>
  )

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden md:flex flex-col w-60 border-r bg-sidebar">
        <div className="flex h-14 items-center px-4 border-b border-sidebar-border gap-2">
          <Shield className="h-5 w-5 text-destructive shrink-0" aria-hidden />
          <span className="font-bold text-sm flex-1">Admin Panel</span>
        </div>

        <div className="px-2 pt-2">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50 transition-colors"
          >
            <ChevronLeft className="h-3 w-3" aria-hidden />
            Back to IntelHub
          </Link>
        </div>

        <Separator className="my-2" />

        <ScrollArea className="flex-1">
          <nav className="flex flex-col gap-4 p-2">
            {adminSections.map((section) => (
              <div key={section.label} className="flex flex-col gap-1">
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.label}
                </div>
                {section.items.map((item) => renderNavItem(item))}
              </div>
            ))}
          </nav>
        </ScrollArea>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex md:hidden h-14 items-center border-b px-4 gap-3">
          <Shield className="h-5 w-5 text-destructive" aria-hidden />
          <span className="font-bold text-sm">Admin Panel</span>
          <Link
            to="/"
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-3 w-3" aria-hidden />
            IntelHub
          </Link>
        </header>

        <div className="px-4 md:px-6 pt-3 pb-0">
          <Breadcrumbs />
        </div>

        <main className="flex-1 overflow-y-auto">
          {/* KAN-857: a refused step-up returns here with ?stepup=failed. */}
          <div className="px-4 md:px-6 pt-3 empty:hidden">
            <StepUpReturnNotice />
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
